'use client';

import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { Deployment } from '@/types/deployment';
import { emptyJwtPayload, JwtPayloadType } from '@/types/jwtTypes';

export type AppContextValue = {
    deployment: Deployment;
    token: string;
    userDecoded: JwtPayloadType;
};

export type AppContextSetters = {
    setToken: (token: string) => void;
    setUserDecoded: (decoded: JwtPayloadType) => void;
};

// Full shape accepted by the provider's `context` prop and used by test helpers.
export type AppContextType = AppContextValue & AppContextSetters;

export const defaultAppContext: AppContextType = {
    deployment: Deployment.Dev,
    setToken: () => {},
    setUserDecoded: () => {},
    token: '',
    userDecoded: { ...emptyJwtPayload },
};

const defaultAppContextValue: AppContextValue = {
    deployment: defaultAppContext.deployment,
    token: defaultAppContext.token,
    userDecoded: defaultAppContext.userDecoded,
};

const defaultAppContextSetters: AppContextSetters = {
    setToken: defaultAppContext.setToken,
    setUserDecoded: defaultAppContext.setUserDecoded,
};

// Split into two contexts so setter-only consumers (e.g. Login) don't re-render
// when token/userDecoded change. Read values with useAppContext, setters with
// useAppContextSetters.
const AppValueContext = createContext<AppContextValue>(defaultAppContextValue);
const AppSettersContext = createContext<AppContextSetters>(defaultAppContextSetters);

type Props = {
    children: ReactNode;
    context: AppContextType;
};

export const AppContextProvider = ({ children, context }: Props) => {
    const [userDecoded, setUserDecoded] = useState<JwtPayloadType>(context.userDecoded);
    const [token, setToken] = useState(context.token);

    // Sync state when context prop changes (e.g., after logout/login). Kept because
    // Login mutates token/userDecoded client-side via the setters without a server
    // round-trip, so prop and state must be reconciled rather than read directly.
    useEffect(() => {
        setToken(context.token);
        setUserDecoded(context.userDecoded);
    }, [context.token, context.userDecoded]);

    const value = useMemo<AppContextValue>(
        () => ({ deployment: context.deployment, token, userDecoded }),
        [context.deployment, token, userDecoded]
    );

    // useState setters are referentially stable, so this object is created once and
    // never causes setter-only consumers to re-render.
    const setters = useMemo<AppContextSetters>(() => ({ setToken, setUserDecoded }), []);

    return (
        <AppSettersContext.Provider value={setters}>
            <AppValueContext.Provider value={value}>{children}</AppValueContext.Provider>
        </AppSettersContext.Provider>
    );
};

export const useAppContext = () => useContext(AppValueContext);
export const useAppContextSetters = () => useContext(AppSettersContext);
